import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Icon, IconName } from './icon'
import { useThemeEffects } from '@/hooks/use-theme-effects'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-lg px-3',
        dense: 'h-8 rounded-lg px-2.5 text-xs font-medium gap-1.5',
        lg: 'h-11 rounded-lg px-8',
        icon: 'h-10 w-10',
        'icon-xs': 'h-6 w-6 rounded-md [&_svg:not([class*="size-"])]:size-3',
        'icon-sm': 'h-7 w-7',
        'icon-dense': 'h-8 w-8 p-0 rounded-lg [&_svg:not([class*="size-"])]:size-3.5',
        'icon-lg': 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  icon?: IconName
  iconPosition?: 'left' | 'right'
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, icon, iconPosition = 'left', loading, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    const isDisabled = disabled || loading
    const { getComponentEffect } = useThemeEffects()
    const buttonEffects = getComponentEffect('button')

    // Build effect classes
    const effectClasses = React.useMemo(() => {
      const classes: string[] = []
      if (buttonEffects?.hover?.glow?.enabled) {
        classes.push('theme-button-glow')
        if (buttonEffects.hover.glow.color) {
          classes.push(`theme-button-glow-${buttonEffects.hover.glow.color}`)
        }
      }
      if ((buttonEffects?.hover as any)?.sparkles?.enabled) {
        classes.push('theme-button-sparkles')
      }
      if ((buttonEffects?.hover as any)?.ripple?.enabled) {
        classes.push('theme-button-ripple')
      }
      return classes
    }, [buttonEffects])

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), ...effectClasses)}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          <>
            <Icon name="loader" size={16} className="animate-spin" />
            {children}
          </>
        ) : (
          <>
            {icon && iconPosition === 'left' && <Icon name={icon} size={16} />}
            {children}
            {icon && iconPosition === 'right' && <Icon name={icon} size={16} />}
          </>
        )}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }

